import React from 'react';
import { CardVariant, ComponentSize } from '../../styles/constants';

export interface CardProps {
  variant?: CardVariant;
  size?: ComponentSize;
  hover?: boolean;
  clickable?: boolean;
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}

export interface CardHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export interface CardContentProps {
  children: React.ReactNode;
  className?: string;
}

export interface CardFooterProps {
  children: React.ReactNode;
  className?: string;
}

export interface CardTitleProps {
  children: React.ReactNode;
  className?: string;
}

const Card: React.FC<CardProps> = ({
  variant = 'elevated',
  size = 'md',
  hover = false,
  clickable = false,
  children,
  className = '',
  onClick,
}) => {
  const baseClasses = `
    rounded-lg transition-all duration-200
    ${clickable ? 'cursor-pointer' : ''}
    ${hover ? 'hover:shadow-lg transform hover:scale-[1.02]' : ''}
  `;

  const sizeClasses = {
    xs: 'p-2',
    sm: 'p-3',
    md: 'p-4',
    lg: 'p-6',
    xl: 'p-8',
  };

  const variantClasses = {
    elevated: `
      bg-white shadow-md border border-secondary-100
      ${hover ? 'hover:shadow-xl' : ''}
    `,
    outlined: `
      bg-white border-2 border-secondary-200
      ${hover ? 'hover:border-secondary-300 hover:shadow-sm' : ''}
    `,
    filled: `
      bg-secondary-50 border border-secondary-200
      ${hover ? 'hover:bg-secondary-100' : ''}
    `,
  };

  const combinedClasses = `
    ${baseClasses}
    ${sizeClasses[size]}
    ${variantClasses[variant]}
    ${className}
  `;

  const handleClick = () => {
    if (clickable && onClick) {
      onClick();
    }
  };

  return (
    <div
      className={combinedClasses}
      onClick={handleClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClick();
              }
            }
          : undefined
      }
    >
      {children}
    </div>
  );
};

const CardHeader: React.FC<CardHeaderProps> = ({
  children,
  className = '',
}) => <div className={`mb-4 ${className}`}>{children}</div>;

const CardContent: React.FC<CardContentProps> = ({
  children,
  className = '',
}) => <div className={`${className}`}>{children}</div>;

const CardFooter: React.FC<CardFooterProps> = ({
  children,
  className = '',
}) => <div className={`mt-4 ${className}`}>{children}</div>;

const CardTitle: React.FC<CardTitleProps> = ({
  children,
  className = '',
}) => <h3 className={`text-lg font-semibold text-gray-900 ${className}`}>{children}</h3>;

// Compound 컴포넌트 패턴 - 타입 정의 추가
const CardWithCompounds = Card as typeof Card & {
  Header: typeof CardHeader;
  Content: typeof CardContent;
  Footer: typeof CardFooter;
  Title: typeof CardTitle;
};

CardWithCompounds.Header = CardHeader;
CardWithCompounds.Content = CardContent;
CardWithCompounds.Footer = CardFooter;
CardWithCompounds.Title = CardTitle;

// Named exports for direct imports
export { Card, CardHeader, CardContent, CardFooter, CardTitle };

export default CardWithCompounds;
